<template>
  <v-card flat class="pa-4 mt-6">
    <v-card-title class="headline">Вебхуки AmoCRM</v-card-title>

    <v-card-text>
      <v-alert v-if="!isAmoCrmEnabled" type="warning" dense>
        Для настройки вебхуков необходимо включить и настроить интеграцию с AmoCRM
      </v-alert>

      <template v-if="isAmoCrmEnabled">
        <v-alert type="info" class="mb-4" outlined>
          Вебхуки позволяют получать данные от AmoCRM в режиме реального времени, когда менеджеры вносят изменения в сделки и контакты.
        </v-alert>

        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">Состояние вебхуков</v-card-title>
              <v-card-text>
                <v-skeleton-loader v-if="loading.state" type="list-item-three-line"></v-skeleton-loader>
                <div v-else>
                  <v-alert
                    :type="webhooksInstalled ? 'success' : 'error'"
                    text
                    dense
                    class="mb-2"
                  >
                    {{ webhooksInstalled ? 'Вебхуки установлены и работают' : 'Вебхуки не установлены' }}
                  </v-alert>

                  <v-list dense class="mb-3">
                    <v-list-item v-if="webhookUrl">
                      <v-list-item-content>
                        <v-list-item-title>URL вебхука:</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ webhookUrl }}
                          <v-icon small @click="copyToClipboard(webhookUrl)" class="ml-1 cursor-pointer">mdi-content-copy</v-icon>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="lastSyncFromAmo">
                      <v-list-item-content>
                        <v-list-item-title>Последняя синхронизация из AmoCRM:</v-list-item-title>
                        <v-list-item-subtitle>{{ lastSyncFromAmo }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>

                  <div class="d-flex">
                    <v-btn
                      color="primary"
                      class="mr-2"
                      @click="setupWebhooks"
                      :loading="loading.setup"
                    >
                      <v-icon left>mdi-link</v-icon>
                      {{ webhooksInstalled ? 'Переустановить вебхуки' : 'Установить вебхуки' }}
                    </v-btn>
                    <v-btn
                      v-if="webhooksInstalled"
                      color="error"
                      @click="removeWebhooks"
                      :loading="loading.removal"
                    >
                      <v-icon left>mdi-link-off</v-icon>
                      Удалить вебхуки
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="subtitle-1">Настройки синхронизации</v-card-title>
              <v-card-text>
                <v-switch
                  v-model="twoWaySync"
                  @change="saveSyncSettings"
                  :loading="loading.settings"
                  label="Двусторонняя синхронизация"
                  hint="При включении изменения будут синхронизироваться в обе стороны"
                  persistent-hint
                ></v-switch>

                <div class="mt-4">
                  <v-btn
                    color="primary"
                    @click="syncFromAmo"
                    :loading="loading.syncFromAmo"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Загрузить данные из AmoCRM
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-expansion-panels v-if="webhooksInstalled && webhooks.length > 0" class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              Активные вебхуки ({{ webhooks.length }})
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">ID</th>
                      <th class="text-left">Назначение</th>
                      <th class="text-left">Сущность</th>
                      <th class="text-left">Событие</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="webhook in webhooks" :key="webhook.id">
                      <td>{{ webhook.id }}</td>
                      <td>{{ webhook.destination }}</td>
                      <td>{{ translateEntity(webhook.settings?.[0]?.entity_type) }}</td>
                      <td>{{ translateAction(webhook.settings?.[0]?.action) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-card-text>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
export default {
  name: 'AmoCrmWebhooks',

  props: {
    companySettings: {
      type: Object,
      default: () => ({
        amocrm_on: false
      })
    }
  },

  data() {
    return {
      webhooks: [],
      webhookUrl: '',
      webhooksInstalled: false,
      twoWaySync: false,
      lastSyncFromAmo: '',
      loading: {
        state: true,
        setup: false,
        removal: false,
        settings: false,
        syncFromAmo: false
      },
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      }
    };
  },

  computed: {
    isAmoCrmEnabled() {
      return this.companySettings.amocrm_on;
    }
  },

  mounted() {
    if (this.isAmoCrmEnabled) {
      this.loadWebhookState();
    }
  },

  methods: {
    loadWebhookState() {
      this.loading.state = true;

      this.axios
        .get('/api/v1/integrations/amocrm_settings', {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.webhooks = response.data.webhooks || [];
          this.webhookUrl = response.data.webhook_url || '';
          this.webhooksInstalled = this.webhooks.length > 0;
          this.twoWaySync = response.data.two_way_sync || false;
          this.lastSyncFromAmo = response.data.last_sync ? new Date(response.data.last_sync).toLocaleString() : 'Никогда';
        })
        .catch(error => {
          console.error('Ошибка при загрузке состояния вебхуков', error);
          this.showSnackbar('Ошибка при загрузке состояния вебхуков', 'error');
        })
        .finally(() => {
          this.loading.state = false;
        });
    },

    setupWebhooks() {
      this.loading.setup = true;

      this.axios
        .post('/api/v1/integrations/amocrm_webhooks/setup', {}, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.showSnackbar('Вебхуки успешно настроены', 'success');
          this.loadWebhookState();
        })
        .catch(error => {
          console.error('Ошибка при настройке вебхуков', error);
          this.showSnackbar(
            error.response?.data?.error || 'Ошибка при настройке вебхуков',
            'error'
          );
        })
        .finally(() => {
          this.loading.setup = false;
        });
    },

    removeWebhooks() {
      this.$swal({
        title: 'Подтверждение',
        text: 'Вы уверены, что хотите удалить все вебхуки AmoCRM? Это приостановит синхронизацию в реальном времени.',
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          this.loading.removal = true;

          this.axios
            .delete('/api/v1/integrations/amocrm_webhooks', {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            })
            .then(response => {
              this.showSnackbar('Вебхуки успешно удалены', 'success');
              this.loadWebhookState();
            })
            .catch(error => {
              console.error('Ошибка при удалении вебхуков', error);
              this.showSnackbar(
                error.response?.data?.error || 'Ошибка при удалении вебхуков',
                'error'
              );
            })
            .finally(() => {
              this.loading.removal = false;
            });
        }
      });
    },

    saveSyncSettings() {
      this.loading.settings = true;

      this.axios
        .put('/api/v1/integrations/amocrm_settings', {
          two_way_sync: this.twoWaySync
        }, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.showSnackbar('Настройки синхронизации сохранены', 'success');
        })
        .catch(error => {
          console.error('Ошибка при сохранении настроек синхронизации', error);
          this.showSnackbar(
            error.response?.data?.error || 'Ошибка при сохранении настроек',
            'error'
          );
          // Восстанавливаем предыдущее значение
          this.twoWaySync = !this.twoWaySync;
        })
        .finally(() => {
          this.loading.settings = false;
        });
    },

    syncFromAmo() {
      this.loading.syncFromAmo = true;

      this.axios
        .post('/api/v1/integrations/amocrm_settings/sync_now', {}, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(response => {
          this.showSnackbar('Синхронизация с AmoCRM запущена', 'success');
          this.lastSyncFromAmo = new Date().toLocaleString();
        })
        .catch(error => {
          console.error('Ошибка при запуске синхронизации', error);
          this.showSnackbar(
            error.response?.data?.error || 'Ошибка при запуске синхронизации',
            'error'
          );
        })
        .finally(() => {
          this.loading.syncFromAmo = false;
        });
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
        .then(() => {
          this.showSnackbar('Скопировано в буфер обмена', 'success');
        })
        .catch(err => {
          console.error('Ошибка при копировании текста: ', err);
        });
    },

    translateEntity(entity) {
      const entities = {
        'lead': 'Сделка',
        'contact': 'Контакт',
        'company': 'Компания',
        'catalog': 'Каталог',
        'product': 'Товар'
      };

      return entities[entity] || entity;
    },

    translateAction(action) {
      const actions = {
        'add': 'Добавление',
        'update': 'Обновление',
        'delete': 'Удаление',
        'status': 'Смена статуса'
      };

      return actions[action] || action;
    },

    showSnackbar(text, color) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
